/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { usePrevious } from "@react-hookz/web";
import { ThemeKey } from "@smartsheet/design-tokens";
import { useResizeObserver, warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useActive } from "@smartsheet/lodestar-utils";
import classNames from "classnames";
import { useCombobox } from "downshift";
import {
    useState,
    useRef,
    InputHTMLAttributes,
    RefAttributes,
    useEffect,
    useCallback,
    ChangeEventHandler,
    KeyboardEvent,
    HTMLAttributes,
} from "react";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps, getInputFieldHeightValue } from "../../../../util/inputFieldBaseProps";
import { useHovered } from "../../../../util/useHovered";
import { Chip } from "../../../chip";
import { ChipColorVariant } from "../../../chip/types";
import { FlyoutProps } from "../../../flyout";
import { useFocusMode } from "../../../focusMode";
import { FORM_LABEL_CLICK_EVENT_ID } from "../../../formlabel/FormLabel";
import { formInputTheme } from "../../../textinput/styled/formInputTheme";
import { SELECT_TOGGLE_ELEMENT_CLASS } from "../../constants";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useAutoHighlightWithLoading } from "../../hooks/useAutoHighlightWithLoading";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { highlightFirstAutomaticallyStateReducer } from "../../reducers/highlightFirstAutomaticallyStateReducer";
import { BaseOptionType, ResetFilteringReason } from "../../types";
import {
    isCreatableMarkerOption,
    creatableMarkerOption,
    stopPropagationHandler,
    useSelectIds,
    isSelectV2OptionSeparator,
    findNextValidOptionIndex,
    squashSeparators,
    sortFilteredItems,
    getChipColor,
} from "../../utils";
import { selectBasicFontTheme, selectV2Theme } from "../selectV2Theme";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButton } from "../shared/ToggleButton";
import { ComboBoxInput } from "./ComboBoxInput";
import { ComboboxTextArea } from "./ComboBoxTextArea";
import { ContentBeforeContainer } from "./ContentBeforeContainer";
import { SelectedOptionFacade } from "./SelectedOptionFacade";
import { SelectedValueWrapper } from "./SelectedValueWrapper";
import { OptionalPropsNowRequired } from "./types";

const warnOnTempFormatSelectedValueWrapperFixUnused = warnOnce(
    "SelectV2 (combobox): If you're using formatSelectedValue, you should also be using" +
        " tempFormatSelectedValueWrapperFix={true} to apply the fix for SelectV2 not" +
        " properly applying `position: absolute` to the wrapper. This prop and warning will be" +
        " removed in a future major version release and the CSS fix will be applied by default."
);

export const PLACEHOLDER_MARGIN_DEFAULT = 12;
export const PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE = 40;
interface SelectPropsBase<Option extends BaseOptionType>
    extends InputFieldBaseProps,
        OptionalPropsNowRequired<Option, HTMLInputElement> {
    onChange: (value: Option | null) => void;
    overrides?: {
        comboInputProps?: HTMLAttributes<HTMLInputElement>;
        comboTextAreaProps?: HTMLAttributes<HTMLTextAreaElement>;
        extraMenuProps?: HTMLAttributes<HTMLDivElement>;
        rootProps?: HTMLAttributes<HTMLDivElement>;
        selectFlyoutProps?: HTMLAttributes<HTMLDivElement> | FlyoutProps;
    };
}

export const SelectComboBox = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    id,
    onChange,
    options,
    renderChipColor,
    selectedOption,
    // optional below
    "aria-label": ariaLabel,
    classes,
    className,
    clientId,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    innerRef,
    inputCharacterLimit,
    inputStyle = {},
    isLoading,
    isOptionDisabled,
    isReadonly,
    isClearable,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuId,
    menuPlacement = "bottom",
    menuPosition,
    onBlur,
    onCreateOption,
    onFocus,
    optionHeight,
    optionToString,
    portalProps,
    filterOption,
    sortFilteredOptions,
    getOptionKey,
    isMultiline,
    validationState,
    menuIsOpen,
    shouldResetFiltering = () => true,
    shouldContentBeforePersist = false,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    inputValue: providedInputValue,
    setInputValue: providedSetInputValue,
    shouldFilterInitialInput = false,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    contentBefore,
    onKeyDown: providedOnKeyDown,
    highlightFirstOptionAutomatically = false,
    onPaste: providedOnPaste,
    tempFormatSelectedValueWrapperFix = false,
    inputFieldHeight,
    shouldDisableClearingOnInputClicks = false,
    overrides,
}: SelectPropsBase<Option>) => {
    if (process.env.NODE_ENV !== "production") {
        if (formatSelectedValue !== undefined && !tempFormatSelectedValueWrapperFix) {
            warnOnTempFormatSelectedValueWrapperFixUnused();
        }
    }

    const a11yMessages = useA11yStatusMessages();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const isCreatable = typeof onCreateOption === "function";
    const isInteractive = !isReadonly;
    const [inputValue, setInputValue] = useState(providedInputValue ?? "");
    const [isFocused, setIsFocused] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<Option[] | null>(null);
    const { ref: resizeRef, width: targetWidth } = useHorizontalResize<HTMLDivElement>();
    const localContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useForkRef(localContainerRef, resizeRef);
    const localInputRef = useRef<HTMLInputElement>(null);
    const toggleButtonRef = useRef<HTMLButtonElement>(null);
    const isKeyboardNavigation = useRef(false);
    const changeType = useRef<string | null>(null);

    const {
        isHovered: isToggleHovered,
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    } = useHovered<HTMLButtonElement>();

    const {
        isActive: isToggleActive,
        onMouseDown: onTAMouseDown,
        onMouseEnter: onTAMouseEnter,
        onMouseLeave: onTAMouseLeave,
    } = useActive({
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    });

    const optionsToDisplay = filteredOptions || options;

    const resetFiltering = (reason: ResetFilteringReason) => {
        if (
            !shouldResetFiltering(
                {
                    highlightedIndex,
                    // pass optionsToDisplay to match highlightedIndex
                    options: optionsToDisplay,
                },
                reason
            )
        ) {
            return;
        }
        setInputValue("");
        providedSetInputValue?.("");
        setFilteredOptions(null);
    };

    const doFilter = useCallback(
        (forInputValue: string) => {
            if (!forInputValue) {
                return;
            }
            const inputLowerCase = forInputValue?.trim().toLowerCase() ?? "";
            const fuzzySearchScoreMap = new Map<Option, number>();
            const filteredItems = options.filter((opt) => {
                if (isSelectV2OptionSeparator(opt)) {
                    return true;
                }
                return filterOption(opt, inputLowerCase, fuzzySearchScoreMap);
            });
            const sortedFilteredItems = sortFilteredOptions
                ? sortFilteredOptions(fuzzySearchScoreMap, filteredItems)
                : sortFilteredItems(fuzzySearchScoreMap, filteredItems);
            const squashedAndFilteredItems = squashSeparators(sortedFilteredItems);
            if (isCreatable) {
                if (
                    squashedAndFilteredItems.filter(
                        (option: Option) => optionToString(option).toLowerCase() === inputLowerCase
                    ).length
                ) {
                    setFilteredOptions(
                        squashedAndFilteredItems.filter((item) => item.value !== creatableMarkerOption.value)
                    );
                    return;
                }
                setFilteredOptions([creatableMarkerOption as unknown as Option, ...squashedAndFilteredItems]);
            } else {
                setFilteredOptions(squashedAndFilteredItems);
            }
        },
        [filterOption, isCreatable, optionToString, options, sortFilteredOptions]
    );

    const [valueFormatWrapperWidth, setValueFormatWrapperWidth] = useState<number | undefined>(undefined);
    const { ref: inputResizeObserverRef } = useResizeObserver<HTMLInputElement>(
        useCallback((entries: ResizeObserverEntry[]) => {
            const inputWidth = entries[0].contentRect.width;
            setValueFormatWrapperWidth(inputWidth);
        }, [])
    );

    const forkedInputRef = useForkRef(localInputRef, inputResizeObserverRef, innerRef || null);
    const selectIds = useSelectIds("combobox", id);

    const {
        isOpen,
        closeMenu,
        openMenu,
        getToggleButtonProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
        selectedItem,
        toggleMenu,
        setHighlightedIndex,
    } = useCombobox({
        ...a11yMessages,
        ...selectIds,
        ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
        circularNavigation: false,
        inputValue,
        items: optionsToDisplay,
        itemToString: (item) => (item === null ? "" : optionToString(item)),
        selectedItem: selectedOption,
        onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem ?? null),
        onInputValueChange: ({ inputValue, isOpen }) => {
            // Don't try and filter when the menu is closed. This can be triggered by
            // the selection changing.
            if (isOpen && inputValue) {
                doFilter(inputValue);
            } else {
                resetFiltering("closed-menu-input-value-change");
            }
        },
        stateReducer: (currentState, actionAndChanges) => {
            const { type, changes } = actionAndChanges;
            changeType.current = type;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                    // Cancel the escape key handling. We want to handle this key on our own
                    // through the StackOrderManager.
                    return currentState;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (changes.selectedItem) {
                        return isOptionDisabled?.(changes.selectedItem) ? currentState : changes;
                    }
                    break;
                // If no results are found and a user closes the menu (either by clicking)
                // the toggle button, the "no results" message, or blur, we want to reset.
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                    if (!selectedItem) {
                        resetFiltering("toggle-menu");
                    }
            }

            if (changes.highlightedIndex) {
                switch (type) {
                    case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                    case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                        if (isSelectV2OptionSeparator(optionsToDisplay[changes.highlightedIndex])) {
                            return {
                                ...changes,
                                highlightedIndex: findNextValidOptionIndex(
                                    options,
                                    changes.highlightedIndex,
                                    type === useCombobox.stateChangeTypes.InputKeyDownArrowDown
                                ),
                            };
                        }
                }
            }
            //look to see if the input has any characters in it,
            //if so then highlightFirstOptionAutomatically can apply its functionality
            const inputHasItems = currentState.inputValue.length > 0 || !!selectedItem;
            if (highlightFirstOptionAutomatically && inputHasItems) {
                return highlightFirstAutomaticallyStateReducer<Option>(
                    optionsToDisplay,
                    currentState,
                    actionAndChanges
                );
            }

            return changes;
        },
        onStateChange: (changes) => {
            switch (changes.type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (changes.selectedItem) {
                        if (isCreatable && isCreatableMarkerOption(changes.selectedItem) && inputValue) {
                            onCreateOption(inputValue);
                        }
                    }
                    // if changes.selectedItem is undefined, it means the user clicked
                    // the same menu item twice
                    else if (!changes.selectedItem && selectedItem) {
                        onChange(selectedItem);
                    }
                    // For any selection, clear things out.
                    resetFiltering("option-selected");
                    break;
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    // Clear filtering on initial state based on controlled props
                    resetFiltering("initial-input-value");
                    break;
                default:
                    break;
            }
        },
        onIsOpenChange: ({ type, isOpen: openState }) => {
            if (process.env.NODE_ENV !== "production") {
                warnOnceForDeprecatedSetMenuIsOpenProp =
                    warnOnceForDeprecatedSetMenuIsOpenProp ??
                    warnOnce(
                        "SelectV2 (combobox): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                    );
                if (setMenuIsOpen) {
                    warnOnceForDeprecatedSetMenuIsOpenProp();
                }
            }
            // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
            (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);

            if (!shouldDisableClearingOnInputClicks) {
                switch (type) {
                    case useCombobox.stateChangeTypes.InputBlur:
                    case useCombobox.stateChangeTypes.FunctionToggleMenu:
                        resetFiltering("input-blur");
                        break;
                }
            }
        },
    });

    const { flyoutProps, targetRef, ariaOwns, ariaControls } = useSelectFlyout({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: containerRef,
        menuPlacement,
        menuPosition,
        portalProps,
        updateKey: isMultiline ? `${String(isOpen)}-${inputValue}` : String(isOpen),
    });

    const commonKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
        providedOnKeyDown?.(e, {
            highlightedIndex,
            // pass optionsToDisplay to match highlightedIndex
            options: optionsToDisplay,
        });
        switch (e.key) {
            case "Tab":
                if (isOpen) {
                    e.preventDefault();
                    localInputRef.current?.focus();
                    closeMenu();
                    resetFiltering("key-down-tab");
                }
                break;
            case "Escape":
                resetFiltering("key-down-escape");
        }
    };

    const downshiftInputProps = getInputProps({
        value: inputValue,
        "aria-describedby": ariaDescribedby,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        "aria-required": ariaRequired,
        onPaste: providedOnPaste,
        onKeyDown: commonKeyDownHandler,
        ref: forkedInputRef,
        "aria-controls": ariaControls,
        /**
         * inputValue has to be updated here due updates in different ticks as normal behavior.
         * Setting inputValue here avoids cursor resetting after input value modifications.
         */
        onChange: useCallback<ChangeEventHandler<HTMLInputElement>>(
            (e) => {
                const val = e.target.value;
                const validInput =
                    // Account for multi-byte characters by using `[].length` instead of `"".length`.
                    inputCharacterLimit && [...val].length > inputCharacterLimit
                        ? val.substring(0, inputCharacterLimit)
                        : val;
                setInputValue(validInput);
                providedSetInputValue?.(validInput);
            },
            [inputCharacterLimit, providedSetInputValue]
        ),
    }) as InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>;

    // Originally the combobox only used an input. When we added multiline support
    // we couldn't break the props referring to a specific type of focusable element
    // (like refs) so we will cast for now until we can ship a breaking change.
    // When we do that, we can add `| HTMLTextAreaElement` to `SelectProps`:
    // SelectProps<Option, HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement>
    const downshiftTextAreaProps = downshiftInputProps as unknown as InputHTMLAttributes<HTMLTextAreaElement> &
        RefAttributes<HTMLTextAreaElement>;

    if (!isInteractive && downshiftInputProps) {
        downshiftInputProps.onChange = undefined;
        downshiftInputProps.onKeyDown = undefined;
        downshiftInputProps.readOnly = true;
    }

    const isContentBeforeVisible: boolean = contentBefore != undefined && (!inputValue || shouldContentBeforePersist);
    const { focusMode } = useFocusMode();

    useEffect(() => {
        if (providedInputValue !== undefined && providedInputValue !== inputValue) {
            setInputValue(providedInputValue);
        }
    }, [inputValue, providedInputValue]);

    // If options changes, we'll have to re-run the filtering step and re-render.
    // This is usually for async-loaded options.
    const previousOptions = usePrevious(options);
    useEffect(() => {
        const isInitialRender = previousOptions === undefined;

        // NOTE: The `shouldFilterInitialInput` prop is crucial for managing the inputValue manually in a grid context.
        // When enabled, it allows manual control over when to trigger the filtering logic based on initial user input.
        // For instance, in a grid scenario, when a user overtypes a character to open the SelectComboBox,
        // the current inputValue is replaced by the overtype character. Enabling `shouldFilterInitialInput` ensures that
        // the options are filtered based on this overtype character
        const shouldFilter = (isInitialRender && shouldFilterInitialInput) || !isInitialRender;

        if (previousOptions !== options && shouldFilter) {
            if (inputValue) {
                doFilter(inputValue);
            } else {
                setFilteredOptions(null);
            }
        }
    }, [doFilter, options, previousOptions, inputValue, shouldFilterInitialInput]);

    const internalHighlightFirstOptionAutomatically =
        highlightFirstOptionAutomatically && (!!inputValue || !!selectedItem);

    useAutoHighlightWithLoading<Option>(
        setHighlightedIndex,
        optionsToDisplay,
        isLoading,
        internalHighlightFirstOptionAutomatically,
        isCreatable,
        inputValue
    );
    const inputHeight = getInputFieldHeightValue(themeMode, inputFieldHeight);
    // This is extracted out since we have a bit of logic we wanted to use here. We had a bug where
    // devs using formatSelectedValue didn't have an absolutely-positioned wrapper to slot their
    // content into. This meant they had to add `position: absolute;` themselves which we didn't
    // want them to have to do. Unfortunately, we can't just add that style ourselves since it would
    // break their existing styles. So we have to do some trickery here to make sure we could allow
    // them to opt into it by using an optional prop that will go away in the next major release.
    const renderSelectedValue = () => {
        const formatted =
            selectedItem &&
            !inputValue &&
            (formatSelectedValue !== undefined ? (
                formatSelectedValue({ option: selectedOption })
            ) : (
                // The important thing here is that this default facade REQUIRES the wrapper below.
                // If the user doesn't specify a formatSelectedValue, we need to make sure we
                // apply the wrapper so it's positioned correctly.
                <SelectedOptionFacade
                    // @ts-expect-error complaining that it doesn't know the string will be right
                    variant={selectBasicFontTheme.variant(themeMode)}
                    isContentBeforeVisible={isContentBeforeVisible}
                    themeMode={themeMode}
                    lineClamp={1}
                >
                    {renderChipColor && selectedOption?.color ? (
                        <Chip
                            size={selectV2Theme.chip.size[inputHeight](themeMode)}
                            color={getChipColor(selectedOption.color as ChipColorVariant)}
                        >
                            {optionToString(selectedOption) || ""}
                        </Chip>
                    ) : (
                        optionToString(selectedItem)
                    )}
                </SelectedOptionFacade>
            ));

        // Use the wrapper by default, or if the user has opted into it.
        const shouldUseWrapper = !formatSelectedValue || tempFormatSelectedValueWrapperFix;

        return shouldUseWrapper ? (
            <SelectedValueWrapper
                isContentBeforeVisible={isContentBeforeVisible}
                themeMode={themeMode}
                style={{ width: valueFormatWrapperWidth }}
            >
                {formatted}
            </SelectedValueWrapper>
        ) : (
            formatted
        );
    };

    /**
     * Downshift does a event.preventDefault on every key press that it cares about,
     * that's how the cursor doesn't move in the input when we navigate the menu
     * on single line select (which is a good thing)
     * That's bad news when we want to navigate the menu in multiline select,
     * so what this does is it determines if downshift should handle the input
     * based on the cursor position of the text area.
     */
    const handleMultilineKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const inputEvent = e as unknown as KeyboardEvent<HTMLInputElement>;

        // these are the keys that we may want to prevent downshift from handling
        const isConditionallyHandledKey =
            e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Home" || e.key === "End" || e.key === "Enter";

        // only navigate menu up if highlighted index is not the first item
        const shouldNavigateMenuUp = (e.key === "ArrowUp" || e.key === "Home") && highlightedIndex > 0;

        // only navigate menu down if the cursor is at the end of the textarea
        const shouldNavigateMenuDown =
            (e.key === "ArrowDown" || e.key === "End") &&
            localInputRef.current?.selectionStart === localInputRef.current?.value.length;

        // only select item if there is no other modifier that is being pressed along with the ENTER key
        const shouldSelectItem = e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey;

        if (shouldSelectItem || shouldNavigateMenuUp || shouldNavigateMenuDown || !isConditionallyHandledKey) {
            // let downshift handle the key press since it isn't an interaction that we care about preserving
            downshiftInputProps.onKeyDown?.(inputEvent);
        } else {
            // downshift automatically stops propagation events to the combobox
            // so we want to preserve them here
            commonKeyDownHandler(inputEvent);
        }
    };

    const handleContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Tab") {
            isKeyboardNavigation.current = true;
        }
    };

    const handleSelectComboBoxFocus = (
        event: React.FocusEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLInputElement>,
        type: "blur" | "focus"
    ) => {
        let isMenu, isInput, isToggleButton, isClearButton;
        if (typeof event?.relatedTarget?.getAttribute === "function") {
            isMenu = event?.relatedTarget?.getAttribute("class")?.includes("sds-select-combobox-menu"); //look for class on menu flyout
            isInput =
                event?.relatedTarget?.getAttribute("class")?.includes("sds-select-combobox-input") ||
                event?.relatedTarget?.tagName === "INPUT"; //look for class on our provided input, or any internal input element.
            isToggleButton = event?.relatedTarget === toggleButtonRef.current;
            isClearButton = event?.relatedTarget?.getAttribute("class")?.includes("sds-select-combobox-clear-button"); //look for class on clear button
        }
        //rule out when the related target is the menu, input or toggle button
        //this way we are only firing the focus events when the container is focused/blurred
        if (!isMenu && !isToggleButton && !isInput && !isClearButton) {
            if (type === "focus") {
                setIsFocused(true);
                isKeyboardNavigation.current = false;
                onFocus?.(event as unknown as React.FocusEvent<HTMLInputElement>);
            } else {
                //in an onBlur event / click outside the component, reset stuff
                setIsFocused(false);
                onBlur?.(event as unknown as React.FocusEvent<HTMLInputElement>);

                // We need to clear the input on blur, but just adding the resetFiltering('input-blur') function here
                // will fire both a 'click-select-clear-button' AND an 'input-blur' filter reason action.
                // to prevent breaking anyone just preventing filtering for the clear button click, we need to
                // know if it's a clear button click blur or a normal blur event
                resetFiltering(isClearButton ? "click-select-clear-button" : "input-blur");
            }
        }
        if (type === "blur" && isInput && isKeyboardNavigation.current) {
            //this happens when the user tabs out, so fire the onBlur event
            setIsFocused(false);
            onBlur?.(event as unknown as React.FocusEvent<HTMLInputElement>);
        }

        //catch-all for focus styles only
        if (type === "focus") {
            setIsFocused(true);
        } else {
            setIsFocused(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            //wait a tick before resetting the changeType
            //we do this so label clicks can distinguish between
            //input blurs that happen when it's clicked vs
            //blurs from other element clicks.
            setTimeout(() => (changeType.current = null), 0);
        }
    }, [isOpen]);

    return (
        // TODO: use the Root component, or align the styles at least. Needs focus states!
        <Root
            inputFieldHeight={inputFieldHeight}
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            focusMode={focusMode}
            themeMode={themeMode}
            appearance={appearance}
            onKeyDown={handleContainerKeyDown}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleSelectComboBoxFocus(e, "blur")}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => handleSelectComboBoxFocus(e, "focus")}
            aria-activedescendant={downshiftInputProps["aria-activedescendant"]}
            aria-controls={downshiftInputProps["aria-controls"]}
            {...getComboboxProps({
                ref: targetRef,
                "aria-owns": ariaOwns,
                onClick: (e) => {
                    e.stopPropagation();
                    //if we know the click is dispatched from the FormLabel,
                    //we want to open the menu if the change isn't the input blur
                    //this is because the input blur fires before the click event
                    //causing the menu to flash closed then reopen (see FormLabel.tsx)
                    if (e.detail === FORM_LABEL_CLICK_EVENT_ID) {
                        if (!isOpen && changeType.current !== useCombobox.stateChangeTypes.InputBlur) {
                            openMenu();
                        }
                        changeType.current = null;
                    } else if (isInteractive) {
                        if (!isOpen) {
                            localInputRef.current?.focus();
                        }
                        // To prevent the menu from opening/closing on every click (even if a user has typed text in)
                        // this call should be gated behind downshiftInputProps.value, only firing when falsy.
                        // To avoid a breaking change, use the shouldDisableClearingOnInputClicks prop to opt-in to this behavior.
                        if (shouldDisableClearingOnInputClicks) {
                            if (!isOpen) {
                                toggleMenu();
                            }
                        } else {
                            toggleMenu();
                        }
                    }
                },
            })}
            {...overrides?.rootProps}
        >
            <HiddenInput
                clientId={clientId}
                hiddenInputName={hiddenInputName}
                getHiddenInputValue={getHiddenInputValue}
                selectionType={selectedOption}
            />
            {isContentBeforeVisible && (
                <ContentBeforeContainer themeMode={themeMode}>{contentBefore}</ContentBeforeContainer>
            )}
            {isMultiline ? (
                <ComboboxTextArea
                    inputFieldHeight={inputFieldHeight}
                    data-client-id={getClientId(clientId, "-input")}
                    readOnly={isReadonly}
                    rows={1}
                    disableManualResize
                    className={classNames(classes?.input, "sds-select-combobox-input", SELECT_TOGGLE_ELEMENT_CLASS)}
                    {...downshiftTextAreaProps}
                    id={id}
                    onKeyDown={handleMultilineKeyDown}
                    //The onChange below complains Type 'FormEventHandler<HTMLTextAreaElement>' is not assignable to type '(value: string, event: ChangeEvent<HTMLTextAreaElement>) => void'
                    //The type on the TextArea is onChange: (value: string, event: React.ChangeEvent<HTMLTextAreaElement>) => void;
                    //@ts-expect-error TS is complaining the type is wrong, it's definitely not.
                    onChange={(_, e) => {
                        if (downshiftTextAreaProps.onChange) {
                            downshiftTextAreaProps.onChange(e);
                        }
                    }}
                    textAreaStyles={{
                        paddingLeft: isContentBeforeVisible ? "0px" : selectV2Theme.input.padding.left(themeMode),
                        // These negate the focus state padding changes in text area since
                        // the Select is already handling that — we don't want to double it.
                        paddingTop: formInputTheme.padding.default.rest(themeMode),
                        paddingBottom: formInputTheme.padding.default.rest(themeMode),
                        ...inputStyle,
                    }}
                    value={inputValue}
                    themeMode={themeMode}
                    {...overrides?.comboTextAreaProps}
                />
            ) : (
                <ComboBoxInput
                    inputFieldHeight={inputFieldHeight}
                    data-client-id={getClientId(clientId, "-input")}
                    readOnly={isReadonly}
                    isReadonly={isReadonly}
                    className={classNames(classes?.input, "sds-select-combobox-input", SELECT_TOGGLE_ELEMENT_CLASS)}
                    isContentBeforeVisible={isContentBeforeVisible}
                    {...downshiftInputProps}
                    id={id}
                    aria-readonly={isReadonly}
                    aria-labelledby={ariaLabelledby}
                    themeMode={themeMode}
                    style={inputStyle}
                    {...overrides?.comboInputProps}
                />
            )}

            {selectedOption === null && !inputValue && placeholder && (
                <SelectPlaceholder inputFieldHeight={inputFieldHeight} hasContentBefore={!!contentBefore}>
                    {placeholder}
                </SelectPlaceholder>
            )}
            {isInteractive && (
                <Actions inputFieldHeight={inputFieldHeight} isReadonly={isReadonly} themeMode={themeMode}>
                    <ToggleButton
                        inputFieldHeight={inputFieldHeight}
                        {...getToggleButtonProps({
                            // Don't let the container handle this.
                            onClick: stopPropagationHandler,
                        })}
                        clientId={clientId}
                        isReadonly={isReadonly}
                        hideDropdownArrow={hideDropdownArrow}
                        isHovered={isToggleHovered}
                        isActive={isToggleActive}
                        isOpen={isOpen}
                        onMouseEnter={onTAMouseEnter}
                        onMouseLeave={onTAMouseLeave}
                        onMouseDown={onTAMouseDown}
                        ref={toggleButtonRef}
                    />

                    {isClearable && selectedOption && (
                        <SelectClearButton
                            inputFieldHeight={inputFieldHeight}
                            refocusTargetElement={() => {
                                localInputRef.current?.focus();
                            }}
                            resetFiltering={resetFiltering}
                            clearSelection={onChange}
                            isMultiSelect={false}
                        />
                    )}
                </Actions>
            )}
            {renderSelectedValue()}
            <SelectMenu<Option>
                allSelected={false}
                enableSelectAll={false}
                flyoutProps={{ ...flyoutProps, tabIndex: -1 } as FlyoutProps}
                formatOption={formatOption}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                className={classNames(classes?.menu, "sds-select-combobox-menu")}
                highlightedIndex={highlightedIndex}
                inputValue={inputValue}
                isLoading={isLoading}
                isOptionDisabled={isOptionDisabled}
                isOpen={isOpen}
                menuWidth={menuWidth}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                options={optionsToDisplay}
                optionToString={optionToString}
                selectedItems={[]}
                selectedOption={selectedOption}
                targetWidth={targetWidth}
                getOptionKey={getOptionKey}
                extraMenuProps={overrides?.extraMenuProps}
                additionalSelectFlyoutProps={overrides?.selectFlyoutProps}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
